import {
  IPayerAutoSelectionRule,
  PayerAutoSelectionRuleKind,
  PayerAutoSelectionSourceScope,
} from "types/PayerAutoSelection/PayerAutoSelection";

/**
 * CLE-2845 — the words on Eligibility › Payer Auto-Selection, in one place so the tab, the dialog
 * and the tester say the same thing.
 */

export interface IStage {
  number: 1 | 2 | 3 | 4;
  kind: PayerAutoSelectionRuleKind | null;
  title: string;
  description: string;
}

export const STAGES: IStage[] = [
  {
    number: 1,
    kind: "MEDICAID_DUAL",
    title: "Medicaid / dual",
    description:
      'A plan that says DUAL or MEDICAID, or whose PolicyType says Medicaid, goes to the facility\'s Medicaid payer, so the patient owes $0. Runs first so a "Medicare dual" plan is not caught by the Medicare Advantage rule.',
  },
  {
    number: 2,
    kind: null,
    title: "Medicare Advantage",
    description:
      'Built in: "WITHOUT MEDICARE" and Medicare Supplement plans never switch; "SPECIAL NEEDS" plans do. Only a Commercial selection switches to its Medicare Advantage twin.',
  },
  {
    number: 3,
    kind: "PRODUCT_KEYWORD",
    title: "Product keyword",
    description:
      "The plan name names another product of the same carrier. Whole words, any comma segment, longest keyword wins, a tie does not switch.",
  },
  {
    number: 4,
    kind: "OUT_OF_AREA_DEFAULT",
    title: "Out-of-area default",
    description:
      "An out-of-state Blue member whose plan name names no product. A product word always beats this.",
  },
];

/** Stage 2's trigger words and its two "never" exclusions, shown on its built-in row. */
export const MEDICARE_ADVANTAGE_TERMS = [
  "MEDICARE",
  "MED ADV",
  "PART C",
  "SPECIAL NEEDS",
];
export const MEDICARE_ADVANTAGE_NEVER = [
  "WITHOUT / NON / NO MEDICARE",
  "SUPPLEMENT",
];

export const KIND_CHOICES: Array<{
  label: string;
  value: PayerAutoSelectionRuleKind;
}> = [
  {
    label: "Medicaid / dual plan → the facility's Medicaid payer",
    value: "MEDICAID_DUAL",
  },
  {
    label: "Plan name names a product → that product's payer",
    value: "PRODUCT_KEYWORD",
  },
  {
    label: "Out-of-state Blue member with no product → default payer",
    value: "OUT_OF_AREA_DEFAULT",
  },
];

export const SOURCE_CHOICES: Array<{
  label: string;
  value: PayerAutoSelectionSourceScope;
}> = [
  {
    label: "Any payer with the same pVerify code (default)",
    value: "SAME_PVERIFY_CODE",
  },
  { label: "Any selected payer, any carrier", value: "ANY_PAYER" },
  { label: "Only these selected payers…", value: "LISTED_PAYERS" },
];

export const OPTION_TEXT = {
  wholeSegment: {
    label: "Bare words must be the whole segment",
    help: 'For FEP\'s "BASIC" and "STANDARD", which are ordinary words elsewhere. A keyword of several words still matches by word.',
  },
  skipOutOfState: {
    label: "Skip out-of-state members (3-digit PlanNumber)",
    help: "For Oklahoma: Georgia's \"BLUE CHOICE\" is not Oklahoma's Blue Choice.",
  },
  prefixMatch: {
    label: "Match as a prefix",
    help: 'For Blue Care, whose native plans read "BLUE CARE BCM0336D …".',
  },
  matchPolicyTypeMedicaid: {
    label: "Also match when pVerify's PolicyType says Medicaid",
    help: "Catches dual plans whose name does not say so.",
  },
};

export const accountsInWords = (rule: IPayerAutoSelectionRule): string =>
  rule.accountIds.length === 0
    ? "All facilities"
    : rule.accounts.map((account) => account.name).join(", ") ||
      `${rule.accountIds.length} facilities`;

export const sourceInWords = (rule: IPayerAutoSelectionRule): string => {
  if (rule.sourceScope === "ANY_PAYER") return "from any selected payer";
  if (rule.sourceScope === "LISTED_PAYERS") {
    return `from ${
      rule.sourcePayers.map((payer) => payer.name).join(", ") ||
      "the listed payers"
    }`;
  }
  return "from any payer with the same pVerify code";
};

export const optionsInWords = (rule: IPayerAutoSelectionRule): string[] =>
  [
    rule.wholeSegment && "bare words must be the whole segment",
    rule.skipOutOfState && "skip out-of-state members",
    rule.prefixMatch && "prefix match",
  ].filter((option): option is string => !!option);

/**
 * The tester's sample responses (real cases from the CLE-2845 analysis). Facility and payer are
 * named, not numbered: the tester finds them by name, so a sample whose facility or payer does not
 * exist here fills the response fields and leaves the selections alone.
 */
export interface ISample {
  label: string;
  /** Matched as a prefix of the facility name ("Ascentist" covers every Ascentist facility). */
  accountName: string;
  payerName: string;
  planName: string;
  payerNameReported: string;
  planNumber: string;
  policyType: string;
}

export const SAMPLES: ISample[] = [
  {
    label: "Westside · SuperMed on Trad",
    accountName: "Westside Surgical Center",
    payerName: "Medical Mutual of Ohio Trad",
    planName: "EYEMED VISION CARE,SUPERMED PLUS CMM - HSA",
    payerNameReported: "Medical Mutual of Ohio",
    planNumber: "",
    policyType: "",
  },
  {
    label: "Lincoln · Network S on Network P",
    accountName: "Lincoln Health",
    payerName: "BCBS TN Network P",
    planName: "BLUE NETWORK S,ADVANCED SPECIALTY BENEFIT MANAGEMENT PROGRAM",
    payerNameReported: "Blue Cross Blue Shield Tennessee",
    planNumber: "MBS12345",
    policyType: "PPO",
  },
  {
    label: "Ascentist · Select Plus on PCB",
    accountName: "Ascentist",
    payerName: "BC Preferred Care Blue",
    planName: "BLUE SELECT PLUS PPK2330A QTRC",
    payerNameReported: "Blue Cross Blue Shield Kansas City",
    planNumber: "",
    policyType: "PPO",
  },
  {
    label: "Ascentist · out-of-state, no product",
    accountName: "Ascentist",
    payerName: "BC Select/Plus",
    planName: "PPO BLUE CARE ELECT",
    payerNameReported: "BCBS of Illinois",
    planNumber: "130",
    policyType: "PPO",
  },
  {
    label: "TEC · UHC dual",
    accountName: "Tuscaloosa Endoscopy Center",
    payerName: "United Healthcare",
    planName:
      "HMOPOS-UHC DUAL COMPLETE AL-D001 (HMO-POS D-SNP),HMO,SPECTERA VISION PLAN",
    payerNameReported: "United Healthcare",
    planNumber: "",
    policyType: "POS,Medicaid,HMO-POS",
  },
  {
    label: "TEC · Humana dual on Humana MA",
    accountName: "Tuscaloosa Endoscopy Center",
    payerName: "Humana Medicare Advantage",
    planName: "HUMANA DUAL SELECT,AFFILIATION/CENTER",
    payerNameReported: "HUMANA",
    planNumber: "",
    policyType: "HMO - Medicare Risk",
  },
  {
    label: 'Baystate · "without Medicare"',
    accountName: "Baystate NE Orthopedic Surgery Center",
    payerName: "HNE (Health New England)",
    planName: "GIC EMPLOYEES/RETIREES WITHOUT MEDICARE",
    payerNameReported: "Health New England Eligibility Standard",
    planNumber: "SH",
    policyType: "",
  },
  {
    label: "Lincoln · Aetna Medicare Advantage",
    accountName: "Lincoln Health",
    payerName: "Aetna",
    planName: "AETNA MEDICARE DUAL CARE (HMO D-SNP)",
    payerNameReported: "AETNA INC",
    planNumber: "",
    policyType: "HMO",
  },
  {
    label: "SHO · Georgia Blue Choice (out of state)",
    accountName: "Surgical Hospital of Oklahoma",
    payerName: "Blue Cross - Blue Preferred",
    planName: "GA PPO BLUE CHOICE",
    payerNameReported: "BCBS of Georgia",
    planNumber: "101",
    policyType: "PPO",
  },
];

export const STAGE_TITLE_BY_REASON: Record<string, string> = {
  MEDICAID_DUAL: "1 · Medicaid / dual",
  MEDICARE_ADVANTAGE: "2 · Medicare Advantage",
  PLAN_NAME: "3 · Product keyword",
  OUT_OF_AREA_DEFAULT: "4 · Out-of-area default",
};
